/**
 * The map of the voyage: the Milky Way from above, drawn on a 2D canvas, with the stops of the letter.
 * It carries the viewer between chapters, hosts the prologue and the epilogue, and collects the measurements.
 */
export interface Stop { id: string; title: string; /** galactic radius in kpc */ r: number; /** azimuth, radians */ a: number; }

const TAU = Math.PI * 2;

export class MapOverlay {
  readonly el: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private dots: Float32Array;   // x, y, brightness, warmth
  private visible = false;
  private alpha = 0;
  private target = 0;
  private traveller = { from: -1, to: -1, t: 1 };
  private stops: Stop[] = [];
  private measurements: string[] = [];
  private rot = 0;
  private title = '';
  private lines: string[] = [];
  private zoom = 1;
  /** horizontal offset of the galaxy's centre, as a fraction of the width */
  private ox = 0;
  private oy = 0;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('canvas'); this.el.className = 'map'; this.el.style.opacity = '0';
    parent.appendChild(this.el);
    this.g = this.el.getContext('2d')!;
    // a fitted galaxy: two arms, a bar, a bulge
    const N = 9000, d = new Float32Array(N * 4); let seed = 11;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) % 4294967296; return seed / 4294967296; };
    const pitch = Math.tan(24 * Math.PI / 180);
    let k = 0;
    while (k < N) {
      const r = -3.2 * Math.log((rnd() || 1e-6) * (rnd() || 1e-6)); if (r > 19) continue;
      const th = rnd() * TAU, phase = Math.log(Math.max(r, 0.3) / 0.3) / pitch;
      let dd = ((th - phase) % Math.PI + Math.PI) % Math.PI; dd = Math.min(dd, Math.PI - dd);
      const w = Math.exp(-((dd / 0.3) ** 2)) * (1 - Math.exp(-r / 2.5));
      if (rnd() > 0.25 + 0.75 * w) continue;
      const bulge = r < 2.2 ? 1 : 0;
      d[k * 4] = r * Math.cos(th); d[k * 4 + 1] = r * Math.sin(th); d[k * 4 + 2] = 0.35 + 0.65 * rnd() * (0.5 + w); d[k * 4 + 3] = bulge ? 1 : 1 - w; k++;
    }
    this.dots = d;
  }

  setStops(stops: Stop[]) { this.stops = stops; }
  setMeasurements(list: string[]) { this.measurements = list; }
  /** Show the map, with a title and lines, moving the traveller marker from one stop index to another. */
  show(title: string, lines: string[], from: number, to: number, zoom = 1, offsetX = 0, offsetY = 0) {
    this.title = title; this.lines = lines; this.traveller = { from, to, t: 0 }; this.visible = true; this.target = 1; this.zoom = zoom; this.ox = offsetX; this.oy = offsetY;
  }
  hide() { this.visible = false; this.target = 0; }
  get isVisible() { return this.visible; }

  /** Per frame. Returns true while the traveller is still moving. */
  update(dt: number): boolean {
    this.alpha += (this.target - this.alpha) * Math.min(1, dt * 3);
    this.el.style.opacity = this.alpha.toFixed(3);
    if (this.alpha < 0.01 && !this.visible) return false;
    this.rot += dt * 0.012;
    if (this.traveller.t < 1) this.traveller.t = Math.min(1, this.traveller.t + dt / 3.2);
    this.draw();
    return this.traveller.t < 1;
  }

  private draw() {
    const c = this.el, dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = Math.round(c.clientWidth * dpr), H = Math.round(c.clientHeight * dpr);
    if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
    const g = this.g; g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = W / dpr, h = H / dpr;
    g.fillStyle = '#04060f'; g.fillRect(0, 0, w, h);
    const cx = w * (0.5 + this.ox), cy = h * (0.5 + this.oy), scale = Math.min(w, h) / 48 * this.zoom;   // 1 kpc in px
    const toXY = (r: number, a: number) => [cx + Math.cos(a + this.rot) * r * scale, cy + Math.sin(a + this.rot) * r * scale * 0.72] as const;
    // disk haze
    const haze = g.createRadialGradient(cx, cy, 0, cx, cy, 20 * scale); haze.addColorStop(0, 'rgba(120,140,200,0.22)'); haze.addColorStop(0.5, 'rgba(90,110,170,0.10)'); haze.addColorStop(1, 'rgba(60,80,140,0)');
    g.fillStyle = haze; g.beginPath(); g.ellipse(cx, cy, 20 * scale, 20 * scale * 0.72, 0, 0, TAU); g.fill();
    // galaxy
    g.globalCompositeOperation = 'lighter';
    for (let i = 0; i < this.dots.length; i += 4) {
      const x = this.dots[i], y = this.dots[i + 1], b = this.dots[i + 2], warm = this.dots[i + 3];
      const r = Math.hypot(x, y), a = Math.atan2(y, x);
      const [px, py] = toXY(r, a);
      g.fillStyle = warm > 0.5 ? `rgba(255,205,140,${0.75 * b})` : `rgba(150,190,255,${0.7 * b})`;
      const s = 1.2 + 1.6 * b;
      g.fillRect(px - s / 2, py - s / 2, s, s);
    }
    // core glow
    const grd = g.createRadialGradient(cx, cy, 0, cx, cy, 3.6 * scale); grd.addColorStop(0, 'rgba(255,215,160,0.95)'); grd.addColorStop(0.35, 'rgba(255,190,120,0.45)'); grd.addColorStop(1, 'rgba(255,190,120,0)');
    g.fillStyle = grd; g.beginPath(); g.ellipse(cx, cy, 3.6 * scale, 2.6 * scale, 0, 0, TAU); g.fill();
    g.globalCompositeOperation = 'source-over';
    // stops and the path
    g.font = '600 12px Anybody, Arial Narrow, sans-serif'; g.textAlign = 'left';
    const pts = this.stops.map((s) => toXY(s.r, s.a));
    g.strokeStyle = 'rgba(227,180,90,0.35)'; g.lineWidth = 1; g.setLineDash([3, 5]); g.beginPath();
    pts.forEach(([x, y], i) => { if (i === 0) g.moveTo(x, y); else g.lineTo(x, y); }); g.stroke(); g.setLineDash([]);
    pts.forEach(([x, y], i) => {
      g.fillStyle = i <= this.traveller.to ? '#e3b45a' : 'rgba(236,232,223,0.5)'; g.beginPath(); g.arc(x, y, 3.5, 0, TAU); g.fill();
      g.fillStyle = 'rgba(236,232,223,0.85)'; g.fillText(this.stops[i].title, x + 8, y + 4);
    });
    // the traveller
    const f = this.traveller.from, tt = this.traveller.to;
    if (tt >= 0 && pts[tt]) {
      const a = f >= 0 && pts[f] ? pts[f] : pts[tt], b = pts[tt];
      const s = this.traveller.t, ease = s * s * (3 - 2 * s);
      const x = a[0] + (b[0] - a[0]) * ease, y = a[1] + (b[1] - a[1]) * ease;
      g.fillStyle = '#ffffff'; g.beginPath(); g.arc(x, y, 5, 0, TAU); g.fill();
      g.strokeStyle = 'rgba(255,255,255,0.6)'; g.lineWidth = 1.5; g.beginPath(); g.arc(x, y, 10 + 3 * Math.sin(performance.now() / 300), 0, TAU); g.stroke();
      g.fillStyle = 'rgba(236,232,223,0.9)'; g.font = '500 11px Anybody, Arial Narrow, sans-serif'; g.fillText('you are here', x + 14, y - 10);
    }
    // the Sun's orbit for scale
    g.strokeStyle = 'rgba(140,193,222,0.25)'; g.lineWidth = 1; g.beginPath(); g.ellipse(cx, cy, 8.2 * scale, 8.2 * scale * 0.72, 0, 0, TAU); g.stroke();
    // title and lines
    const left = Math.max(16, w * 0.06);
    g.fillStyle = '#ece8df'; g.font = `800 ${Math.min(44, w * 0.05)}px Anybody, Arial Narrow, sans-serif`; g.fillText(this.title, left, h * 0.16);
    g.font = `italic 400 ${Math.min(19, w * 0.022)}px "Source Serif 4", Georgia, serif`; g.fillStyle = 'rgba(236,232,223,0.9)';
    let y = h * 0.16 + 34;
    for (const l of this.lines) { y = this.wrap(g, l, left, y, Math.min(w * 0.46, 520), 26); y += 8; }
    // measurements so far
    if (this.measurements.length) {
      g.textAlign = 'right'; const right = w - left;
      g.font = '500 12px Anybody, Arial Narrow, sans-serif'; g.fillStyle = 'rgba(227,180,90,0.9)';
      let my = h * 0.16;
      g.fillText('Measured so far', right, my);
      for (const m of this.measurements) { my += 18; g.fillStyle = 'rgba(236,232,223,0.85)'; g.fillText(m, right, my); }
      g.textAlign = 'left';
    }
  }

  private wrap(g: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lh: number): number {
    const words = text.split(' '); let line = '';
    for (const w of words) { const t = line + w + ' '; if (g.measureText(t).width > maxW && line) { g.fillText(line, x, y); line = w + ' '; y += lh; } else line = t; }
    g.fillText(line, x, y); return y + lh;
  }
}
